import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './LLMSolutionPage.scss';
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import CTA from "../../components/CTA/CTA";

const LLMSolutionPage = () => {
    const [activeSection, setActiveSection] = useState('overview');

    const sections = {
        overview: {
            title: "Overview",
            content: (
                <div className="section-content">
                    <p className="lead">
                        Since the rise of Large Language Models (LLMs), only one approach has consistently delivered real, scalable business value: LLM + Retrieval-Augmented Generation (RAG).
                    </p>
                    <div className="value-cards">
                        <div className="value-card">
                            <div className="icon">🚀</div>
                            <h3>Scalable Performance</h3>
                            <p>Handle millions of queries with enterprise-grade reliability</p>
                        </div>
                        <div className="value-card">
                            <div className="icon">💰</div>
                            <h3>Cost-Effective</h3>
                            <p>Optimized infrastructure for maximum ROI</p>
                        </div>
                        <div className="value-card">
                            <div className="icon">🔒</div>
                            <h3>Enterprise Ready</h3>
                            <p>Built with security and compliance in mind</p>
                        </div>
                    </div>
                </div>
            )
        },
        useCases: {
            title: "Use Cases",
            content: (
                <div className="section-content">
                    <div className="use-cases-grid">
                        <div className="use-case-card">
                            <h3>Document Intelligence</h3>
                            <ul>
                                <li>Legal document analysis</li>
                                <li>Financial report processing</li>
                                <li>Technical documentation search</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Customer Support</h3>
                            <ul>
                                <li>24/7 multilingual support</li>
                                <li>Automated ticket resolution</li>
                                <li>Knowledge base integration</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Knowledge Management</h3>
                            <ul>
                                <li>Employee onboarding</li>
                                <li>Internal knowledge base</li>
                                <li>Training and documentation</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Compliance & Audit</h3>
                            <ul>
                                <li>Regulatory compliance checks</li>
                                <li>Document verification</li>
                                <li>Audit trail generation</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        technical: {
            title: "Technical",
            content: (
                <div className="section-content">
                    <p className="lead">
                        We don't just plug into APIs. Most providers stop at calling OpenAI or Anthropic — <strong>we go further</strong> with infrastructure and modeling tailored to your business:
                    </p>
                    <div className="tech-features-grid">
                        <div className="tech-feature">
                            <div className="feature-icon">🤖</div>
                            <div className="feature-content">
                                <h3>Custom Models</h3>
                                <p>Domain-specific LLMs (1.5B–7B) fine-tuned for your unique requirements</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">🔍</div>
                            <div className="feature-content">
                                <h3>Advanced Retrieval</h3>
                                <p>State-of-the-art semantic search with vector and graph databases</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">⚡</div>
                            <div className="feature-content">
                                <h3>Hybrid Architecture</h3>
                                <p>Optimized combination of vector, relational, and graph stores</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">📊</div>
                            <div className="feature-content">
                                <h3>Performance Optimization</h3>
                                <p>Low-latency infrastructure with cost-aware design</p>
                            </div>
                        </div>
                    </div>
                    <p className="note">
                        For clients with sufficient computational resources, we also offer SFT (Supervised Fine-Tuning) capabilities for 32B parameter models to achieve even higher performance.
                    </p>
                    <p className="conclusion">
                        This full-stack approach ensures your AI system is not just intelligent, but dependable, efficient, and ready for production.
                    </p>
                </div>
            )
        },
        cases: {
            title: "Case Studies",
            content: (
                <div className="section-content">
                    <div className="case-studies-grid">
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>Intelligent Menu Q&A System</h3>
                                <p>Combining intent recognition, entity recognition, and knowledge base retrieval with RAG.</p>
                            </div>
                        </div>
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>Vehicle Manual Assistant</h3>
                                <p>AI-powered system for instant access to vehicle owner's manual information.</p>
                            </div>
                        </div>
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>LLM Model Optimization</h3>
                                <p>Quantizing llama3-8b to W4A16 for efficient deployment.</p>
                            </div>
                        </div>
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>Appointment Scheduling Agent</h3>
                                <p>Intelligent calendar management and scheduling assistant.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    };

    return (
        <div className="solution-page">
            <Helmet>
                <title>LLM & RAG Solutions - Majority AI | Large Language Model Services</title>
                <meta name="description" content="Expert LLM and RAG solutions by Majority AI. Custom chatbots, document processing, and intelligent automation using cutting-edge language models." />
                <meta name="keywords" content="LLM solutions, RAG solutions, large language models, chatbot development, document AI, MajorityAI LLM" />
                <link rel="canonical" href="https://www.majorityai.com/llm-rag-solutions" />
            </Helmet>
            <Navbar/>
            <section className="slide-card-header">
                <div className='wrapper'>
                    <div className="outer-frame"></div>
                    <div className="inner-container">
                        <h1>LLM + RAG Solutions</h1>
                    </div>
                </div>
            </section>

            <section className="content-section">
                <div className="wrapper2">
                    <div className="section-navigation">
                        {Object.entries(sections).map(([key, section]) => (
                            <button
                                key={key}
                                className={`nav-button ${activeSection === key ? 'active' : ''}`}
                                onClick={() => setActiveSection(key)}
                            >
                                {section.title}
                            </button>
                        ))}
                    </div>

                    <div className="section-container">
                        {sections[activeSection].content}
                    </div>

                    <CTA />
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default LLMSolutionPage;
